import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { adminListTags, adminCreateTag, adminUpdateTag, adminDeleteTag } from "@/lib/blog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";

export default function AdminBlogTags() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const load = async () => { setLoading(true); try { setItems(await adminListTags()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); if (!draft) return; try { await adminCreateTag({ name: draft }); setDraft(""); toast.success("Tag created"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } };
  const onPatch = (id, patch) => setItems((prev) => prev.map((t) => t.id === id ? { ...t, ...patch, _dirty: true } : t));
  const saveOne = async (t) => { await adminUpdateTag(t.id, { name: t.name, slug: t.slug }); toast.success("Saved"); load(); };
  const delOne = async (t) => { if (!window.confirm(`Delete tag "${t.name}"?`)) return; await adminDeleteTag(t.id); toast.success("Deleted"); load(); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-blog-tags">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => nav("/admin/blog")} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1.5" />Blog</Button>
          <Logo />
          <span className="inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">Tags</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-6">
        <section className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="font-bold text-slate-900 mb-3">Add tag</h2>
          <form onSubmit={create} className="flex gap-2" data-testid="tag-create-form">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Tag name" required data-testid="tag-name" />
            <Button type="submit" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="tag-create-submit"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </form>
        </section>
        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
            : items.length === 0 ? <div className="py-10 text-center text-slate-500 text-sm">No tags yet.</div>
            : (
              <ul className="divide-y divide-slate-100" data-testid="tag-list">
                {items.map((t) => (
                  <li key={t.id} className="p-3 flex flex-col sm:grid sm:grid-cols-[1fr_1fr_60px_auto] gap-2 items-start sm:items-center" data-testid={`tag-row-${t.slug}`}>
                    <Input value={t.name} onChange={(e) => onPatch(t.id, { name: e.target.value })} />
                    <Input value={t.slug} onChange={(e) => onPatch(t.id, { slug: e.target.value })} />
                    <span className="text-xs text-slate-500 text-center">{t.post_count}</span>
                    <div className="flex gap-1.5 justify-end">
                      {t._dirty && <Button size="sm" onClick={() => saveOne(t)} className="bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button>}
                      <Button size="sm" variant="ghost" onClick={() => delOne(t)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </section>
      </main>
    </div>
  );
}
